import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SearchIcon from '@mui/icons-material/Search';
import axios from 'axios';
import { useFormikContext } from 'formik';
import { isEmpty } from 'lodash';

import { pickEditableIdentityFields } from '~/components/Contacts/UpsertContact/utils';
import Button from '~/components/core/Atomic/Buttons/Button';
import IconButton from '~/components/core/Atomic/Buttons/IconButton';
import InputAdornment from '~/components/core/Atomic/InputAdornment';
import List from '~/components/core/Atomic/List';
import ListItem from '~/components/core/Atomic/ListItem';
import ListItemText from '~/components/core/Atomic/ListItemText';
import MenuItem from '~/components/core/Atomic/MenuItem';
import Tooltip from '~/components/core/Atomic/Tooltip';
import LEGACY_ORG_FEATURES from '~/server_shared/generated-types/LEGACY_ORG_FEATURES';

import { isLegacyOrgFeatureEnabled, reportAxiosError } from '../../Utils';
import CardDialog from '../CardDialog';
import { openTopLevelDialog } from '../CmsMain/globals';
import { ConfirmModal } from '../ConfirmModal';
import { PERMISSION_ACTIONS, PERMISSION_VERBS } from '../core';
import { ErrorHelperTextFormik } from '../core/Formik/ErrorHelperTextFormik';
import { useCms } from '../hooks/useCms';
import LoadingDialog from '../LoadingDialog';
import { TextFieldFormik } from '../TextFieldFormik';

import { useSanitizeByPermissions } from './useHasPermission';

import { useStyles } from '../../assets/styles';

export const CLAIM_TYPE = {
  wc_claim: 'wc_claim',
  gl_claim: 'gl_claim',
};

export const handleAutoFillItrakValues = async ({
  user,
  values,
  setFieldValue,
  setFieldTouched,
  setItrakContactsList,
  setLoading,
  involvedPersonId,
  claimType,
  setInitialValuesFilled,
}) => {
  try {
    if (!values['itrak_number']) return;

    setLoading(true);

    const { data } = await axios.get(`/api/v1/organizations/${user.organization_id}/itrak_autofill`, {
      params: { itrak_number: values['itrak_number'], claim_type: claimType },
    });
    Object.entries(data).forEach(([key, value]) => {
      setFieldValue(key, value);
      setFieldTouched(key, true);

      if (key === 'involved_person') {
        setItrakContactsList(value);
      }

      ['primary_contact', 'reporter_contact'].forEach((key) => {
        if (isEmpty(values[key]) && !isEmpty(data[key])) {
          setFieldValue(`${key}_id`, data[key]?.id || '');
          setFieldValue(`${key}_full_name`, data[key]?.full_name || '');
          setFieldValue(key, data[key]);
        }
      });

      if (key === 'supervisor_contact') {
        if (isEmpty(values[key]) && !isEmpty(data[key])) {
          setFieldValue(`${involvedPersonId}.${key}_id`, data[key]?.id || '');
          setFieldValue(`${involvedPersonId}.${key}_full_name`, data[key]?.full_name || '');
          setFieldValue(`${involvedPersonId}.${key}`, data[key]);
        }
      }
    });

    if (!isEmpty(data?.error)) {
      openTopLevelDialog({ title: 'Error', message: data.error });
    }

    setInitialValuesFilled(true);
  } catch (error) {
    reportAxiosError(error);
  }

  setLoading(false);
};

export const handleUploadItrakMediaFiles = async ({
  user,
  values,
  setFieldValue,
  setFieldTouched,
  setLoading,
  setUploadedPhotos,
}) => {
  try {
    if (!values['itrak_number']) return;

    setLoading(true);

    const { data } = await axios.post(
      `/api/v1/organizations/${user.organization_id}/itrak_autofill/upload_itrak_media`,
      { params: { itrak_number: values['itrak_number'], files_info_list: values['files_info_list'] } }
    );
    Object.entries(data).forEach(([key, value]) => {
      if (key === 'stored_files') {
        setFieldTouched(key, true);
        setFieldValue('stored_files_ids', [...values['stored_files_ids'], ...value.map((photo) => photo.id)]);
        setUploadedPhotos(value);
      }
    });

    if (!isEmpty(data?.error)) {
      openTopLevelDialog({ title: 'Error', message: data.error });
    }
  } catch (error) {
    reportAxiosError(error);
  }

  setLoading(false);
};

const handleInvolvedPersonSelect = async ({
  value,
  itrakContactsList,
  setFieldValue,
  setFieldTouched,
  values,
  involvedPersonId,
  organizationId,
  sanitizePayload,
}) => {
  const involvedPerson = itrakContactsList.find((item) => item.id === value);
  Object.keys(involvedPerson).forEach((key) => {
    if (involvedPerson[key]) {
      setFieldValue(`${involvedPersonId}.${key}`, involvedPerson[key]);
      setFieldTouched(`${involvedPersonId}.${key}`, true);
    }
  });

  setFieldValue('date_hired', involvedPerson.date_hired);
  setFieldValue('termination_date', involvedPerson.termination_date);
  setFieldValue(`${involvedPersonId}.contact`, involvedPerson.employee_contact);
  setFieldValue(`${involvedPersonId}.select_involved_person`, involvedPerson.employee_contact);

  ['primary_contact', 'reporter_contact'].forEach((key) => {
    if (isEmpty(values[key])) {
      setFieldValue(`${key}_id`, involvedPerson ? involvedPerson.id : '');
      setFieldValue(`${key}_full_name`, involvedPerson?.full_name);
      setFieldValue(key, involvedPerson);
    }
  });

  if (involvedPerson.role !== involvedPersonId) {
    try {
      const { data: contactData } = await axios.get(
        `/api/v1/organizations/${organizationId}/contacts/${involvedPerson.id}`
      );
      const role = involvedPersonId === 'involved_employee' ? 'injured_employee' : 'claimant';
      contactData.role = role;
      contactData.reason = 'typo';
      contactData.explanation = '';

      const payload = sanitizePayload(pickEditableIdentityFields(contactData));

      await axios.patch(`/api/v1/organizations/${organizationId}/contacts/${involvedPerson.id}/v2`, {
        identity: payload,
      });
    } catch (error) {
      reportAxiosError(error);
    }
  }
};

const ItrakNumber = () => {
  const { classes } = useStyles();

  return <TextFieldFormik id="itrak_number" label="ITrak number" className={classes.textField} fullWidth />;
};

const ItrakSearch = ({ setItrakContactsList, involvedPersonId, claimType, setInitialValuesFilled }) => {
  const { classes } = useStyles();
  const { user } = useCms();
  const { setFieldValue, setFieldTouched, values } = useFormikContext();
  const [isLoading, setLoading] = useState(false);
  const handleAutoFill = () =>
    handleAutoFillItrakValues({
      user,
      values,
      setFieldValue,
      setFieldTouched,
      setItrakContactsList,
      setLoading,
      involvedPersonId,
      claimType,
      setInitialValuesFilled,
    });

  return (
    <>
      {isLoading && <LoadingDialog isError={false} track="Itrak Loading" text="Loading Itrak Details" />}
      <TextFieldFormik
        id="itrak_number"
        label="ITrak number"
        className={classes.textField}
        fullWidth
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton style={{ padding: 4 }} onClick={handleAutoFill}>
                <Tooltip title="Autofill Itrak Details">
                  <SearchIcon fontSize="small" />
                </Tooltip>
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    </>
  );
};

ItrakSearch.propTypes = {
  setItrakContactsList: PropTypes.func,
  involvedPersonId: PropTypes.string.isRequired,
  setUploadedPhotos: PropTypes.func,
  setInitialValuesFilled: PropTypes.func,
  claimType: PropTypes.string.isRequired,
};

const InvolvedPersonSelect = ({ itrakContactsList, involvedPersonId }) => {
  const { classes } = useStyles();
  const { user } = useCms();
  const { handleChange, setFieldValue, setFieldTouched, values } = useFormikContext();
  const sanitizePayload = useSanitizeByPermissions({
    fieldsPermissionActions: {
      date_of_birth: PERMISSION_ACTIONS.CONTACT_PII,
      government_id: PERMISSION_ACTIONS.CONTACT_PII,
    },
    verb: PERMISSION_VERBS.WRITE,
    forbiddenFields: ['tin'],
  });

  const handleInvolvedPersonSelectChange = (value) =>
    handleInvolvedPersonSelect({
      value,
      itrakContactsList,
      setFieldValue,
      setFieldTouched,
      values,
      involvedPersonId,
      organizationId: user.organization_id,
      sanitizePayload,
    });
  const involvedPersonLabel = involvedPersonId === 'involved_employee' ? 'Employee' : 'Claimant';

  useEffect(() => {
    setFieldValue(`${involvedPersonId}.involved_person_filled`, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <TextFieldFormik
        id="select_involved_person"
        label={`Select ${involvedPersonLabel}`}
        className={classes.textField}
        fullWidth
        select
        onChange={async (event) => {
          await handleInvolvedPersonSelectChange(event.target.value);
          handleChange(event);
          setFieldValue(`${involvedPersonId}.involved_person_filled`, true);
        }}
      >
        {itrakContactsList.map(({ id, label }) => (
          <MenuItem key={id} value={id}>
            {label}
          </MenuItem>
        ))}
      </TextFieldFormik>
      <ErrorHelperTextFormik id={`${involvedPersonId}.involved_person_filled`} />
    </>
  );
};

InvolvedPersonSelect.propTypes = {
  itrakContactsList: PropTypes.array.isRequired,
  involvedPersonId: PropTypes.string.isRequired,
};

const ItrakUploadMediaFiles = () => {
  const { classes } = useStyles();
  const { user } = useCms();
  const { setFieldValue, setFieldTouched, values } = useFormikContext();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false); // not using WithConfirm due to issue with rendering children dynamically
  const [isLoading, setLoading] = useState(false);
  const [uploadedPhotos, setUploadedPhotos] = useState([]);
  const filesNames = !isEmpty(values['files_info_list'])
    ? values['files_info_list'][0].map(({ file_name }) => file_name)?.join(', ')
    : '';

  const handleUploadFiles = async () => {
    setIsConfirmOpen(false);
    await handleUploadItrakMediaFiles({ user, setFieldValue, setFieldTouched, values, setLoading, setUploadedPhotos });
  };

  return (
    <CardDialog title="Upload Itrak Media Files">
      {isLoading && <LoadingDialog isError={false} track="Itrak Media Loading" text="Uploading Itrak Media Files" />}
      <ConfirmModal
        isOpen={isConfirmOpen}
        title="Itrak media files?"
        contentText={
          <>
            This operation will upload the following media files:
            <br />
            {filesNames}
            <br />
            This operation might take several minutes to complete.
          </>
        }
        primaryButtonName="Upload"
        onPrimaryBtnClick={handleUploadFiles}
        onClose={() => setIsConfirmOpen(false)}
      />
      {isEmpty(uploadedPhotos) && (
        <Button color="primary" onClick={() => setIsConfirmOpen(true)}>
          <CloudUploadIcon className={classes.leftButtonIcon} />
          Upload Itrak Files
        </Button>
      )}
      {uploadedPhotos.length > 0 && (
        <List dense>
          {uploadedPhotos.map((photo) => (
            <ListItem key={photo.id}>
              <ListItemText primary={photo.stored_file_extra?.document_name || photo.original_filename} />
            </ListItem>
          ))}
        </List>
      )}
    </CardDialog>
  );
};

const useItrak = ({ claimType }) => {
  const { userOrganization } = useCms();
  const [initialValuesFilled, setInitialValuesFilled] = useState(false);
  const [itrakContactsList, setItrakContactsList] = useState([]);
  const isItrakAutofillEnabled = isLegacyOrgFeatureEnabled(userOrganization, LEGACY_ORG_FEATURES.MGM_ITRAK_AUTOFILL);
  const involvedPersonId =
    claimType === CLAIM_TYPE.wc_claim
      ? 'involved_employee'
      : claimType === CLAIM_TYPE.gl_claim
        ? 'involved_claimant'
        : undefined;
  const ItrakComponent = () =>
    isItrakAutofillEnabled ? (
      <ItrakSearch
        involvedPersonId={involvedPersonId}
        setItrakContactsList={setItrakContactsList}
        claimType={claimType}
        setInitialValuesFilled={setInitialValuesFilled}
      />
    ) : (
      <ItrakNumber />
    );
  const ItrakInvolvedPersonSelect =
    isItrakAutofillEnabled && !isEmpty(itrakContactsList)
      ? () => <InvolvedPersonSelect itrakContactsList={itrakContactsList} involvedPersonId={involvedPersonId} />
      : undefined;
  const ItrakUploadFiles = () => (isItrakAutofillEnabled && initialValuesFilled ? <ItrakUploadMediaFiles /> : null);

  return {
    ItrakComponent,
    isItrakAutofillEnabled,
    itrakContactsList,
    ItrakInvolvedPersonSelect,
    ItrakUploadFiles,
  };
};

useItrak.CLAIM_TYPE = CLAIM_TYPE;

export default useItrak;
